import { useState } from "react";

/**
 * Chips input for an enum-style value list. Type a value and press Enter (or
 * comma) to commit it as a chip; × removes one; Backspace on the empty field
 * removes the last. Duplicates are ignored case-insensitively. The author
 * focuses on entering values, not on getting the formatting right.
 *
 * Shared by the flow editor (choice options, branch values, never-say/topic
 * phrases) and the slot catalog (choice options), so the chip behaviour stays
 * identical across the app.
 */
export function ChipsInput({
  value,
  onChange,
  placeholder,
  title,
  clean: cleanProp,
}: {
  value: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  title?: string;
  /**
   * Sanitize each keystroke/committed chip. Defaults to preserving any visible
   * character (letters, numbers, spaces, and punctuation like - + / & ( ) . '),
   * stripping only the comma (our chip separator) and control chars. These chips
   * hold human-facing VALUES — choice options ("Long-distance", "2-4 weeks",
   * "3+ bedroom"), branch-condition values, and never-say/topic phrases — so
   * punctuation must survive, and a branch value must be able to match its option
   * verbatim. Callers that need identifier-style input (lowercase, no spaces)
   * pass their own stricter `clean`.
   */
  clean?: (s: string) => string;
}) {
  const [draft, setDraft] = useState("");
  const clean = cleanProp ?? ((s: string): string => s.replace(/[,\r\n\t]/g, ""));

  const commit = (raw: string) => {
    const v = clean(raw).trim();
    setDraft("");
    if (!v) return;
    if (value.some((x) => x.toLowerCase() === v.toLowerCase())) return; // dedupe
    onChange([...value, v]);
  };

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-inset focus-within:ring-brand-500 dark:border-zinc-800 dark:bg-zinc-950/40"
      title={title}
      onClick={(e) => e.currentTarget.querySelector("input")?.focus()}
    >
      {value.map((v) => (
        <span
          key={v}
          className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
        >
          {v}
          <button
            type="button"
            aria-label={`Remove ${v}`}
            className="leading-none text-brand-500 hover:text-brand-800 dark:hover:text-brand-100"
            onClick={(e) => {
              e.stopPropagation();
              onChange(value.filter((x) => x !== v));
            }}
          >
            ×
          </button>
        </span>
      ))}
      <input
        className="min-w-[90px] flex-1 bg-transparent text-[12px] text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-zinc-100"
        value={draft}
        placeholder={value.length === 0 ? placeholder : ""}
        onChange={(e) => {
          const val = e.target.value;
          // Paste (or autofill) with commas → commit all complete chips, keep
          // the trailing fragment as the live draft.
          if (val.includes(",")) {
            const parts = val.split(",");
            const last = parts.pop() ?? "";
            parts.forEach(commit);
            setDraft(clean(last));
          } else {
            setDraft(clean(val));
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit(draft);
          } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => commit(draft)}
      />
    </div>
  );
}
